import Insight from "../insightClass";
import React from "react";
import { insight63Media } from "../../../assets/assetReference";
import { ContactSubscribe } from "../../../components/Contact/Contact";
export const insight063 = new Insight(
  "05 September 2025",
  {
    pageTitle:
      "China's Deep-Water Offshore Wind: From Policy Reset to Multi-Province Build-out (2024-2030)",
    metaDescription:
      "WeBridge Insights, China's Deep-Water Offshore Wind: From Policy Reset to Multi-Province Build-out (2024-2030)",
    keyWords:
      "European Offshore Wind Market development, China's Deep-Water Offshore Wind: From Policy Reset to Multi-Province Build-out (2024-2030)",
  },
  {
    src: insight63Media[0],
    alt: "offshore wind turbine",
  },
  "China's Deep-Water Offshore Wind: From Policy Reset to Multi-Province Build-out (2024-2030)",
  "",
  `China's "dual-carbon" goals have nudged offshore wind decisively into deeper, bluer waters. Since late-2024, a national push has tightened siting rules (deeper and farther from shore), which in turn is steering provinces to the 50 m-plus "deep-sea" belt and accelerating high-voltage export systems and floating foundations.`,
  <div>
    <strong>WeBridge Insights</strong>
    <h1>
      China's Deep-Water Offshore Wind: From Policy Reset to Multi-Province
      Build-out (2024-2030)
    </h1>
    <h2></h2>
    <p className="article-date">05 September 2025</p>
    <img src={insight63Media[0]} alt="Goldwind 16MW+ floating turbine" />
    <br />
    <p>
      China's "dual-carbon" goals have nudged offshore wind decisively into
      deeper, bluer waters. Since late-2024, a national push has tightened
      siting rules (deeper and farther from shore), which in turn is steering
      provinces to the 50 m-plus "deep-sea" belt and accelerating high-voltage
      export systems and floating foundations.
    </p>

    <h3>Why 2025 looks like "year one" for deep-sea</h3>
    <ul>
      <li>
        <b>Policy reset</b>: New national guidance requires most new projects to
        be ≥30 km offshore or in water deeper than 30 m—formally hard-wiring the
        move away from crowded near-shore waters.
      </li>
      <li>
        <b>Provincial pipelines maturing</b>: Guangdong's 2023 scheme
        pre-selected 16 GW of national-sea sites (8 GW flagged as pilots), with
        competitive allocation running in parallel on provincial sites—providing
        immediate room to grow in 2024-2026.
      </li>
      <li>
        <b>Technology catch-up</b>: Provinces are now ordering and trialling
        ±500 kV flexible HVDC export and first-wave floating pilots sized for
        typhoon seas; multiple orders reference Qingzhou-5/7 and Sanzhandao
        clusters in Guangdong.
      </li>
    </ul>

    <h3>Province-by-province pulse check</h3>
    <h4 className="font-bold" style={{ marginTop: "36px" }}>
      Guangdong (leader / tech proving ground).
    </h4>
    <p>
      The 2023 competitive-allocation plan set out 16 GW of pre-selected
      national-sea sites (Shantou/Shanwei/Jieyang/Chaozhou) with 8 GW identified
      for demonstration, alongside 7 GW in provincial waters. Recent supply
      awards confirm ±500 kV flexible-DC export cables for Yangjiang clusters
      (Qingzhou 5/7; Sanzhandao), consistent with large, far-offshore
      build-outs. Floating prototypes such as the 16.6 MW "Flying/MYX" platform
      reached operation, underlining typhoon-class design ambitions.
    </p>

    <h4 className="font-bold" style={{ marginTop: "36px" }}>
      Jiangsu (restart after a near-shore pause).
    </h4>
    <p>
      Project activity has restarted on big near-to-mid-shelf schemes—e.g., CTG
      Dafeng 800 MW signed turbine contracts to resume works; Guoxin Dafeng 850
      MW set a 2025 COD target. Grid planners are tracking roughly 8 GW of new
      offshore wind, and Jiangsu has also advanced 5.8 GW of deep-sea sites to
      early-work procurement (Z-blocks). This collectively points to a return to
      top-tier additions from 2025, with deep-sea focus increasing through the
      "15th Five-Year Plan".
    </p>

    <h4 className="font-bold" style={{ marginTop: "36px" }}>
      Zhejiang (scale and speed).
    </h4>
    <p>
      Targets now split 8.5 GW (provincial) + 8 GW (national-sea) through 2025;
      in September 2024 the province capitalised a ¥6 bn offshore wind company
      to coordinate ~28 GW of deep-sea sites. Industry trackers report the first
      2 GW deep-sea demonstration has already completed major equipment tenders
      (turbines, sea cables, onshore HVDC valves), setting up full-fleet
      grid-connection around 2026.
    </p>

    <h4 className="font-bold" style={{ marginTop: "36px" }}>
      Shanghai (short coastline, long reach).
    </h4>
    <p>
      City planners are leaning into deep-sea plots east of Chongming. The NDRC
      posted the 2024 offshore wind project list, including Deep-Sea Project #2;
      several research notes state a ~29.3 GW deep-sea plan received state-level
      approval (I flag this as reported rather than published in full), while
      the city is running competitive allocations and technical pilots that
      validate large floating foundations and higher-voltage export.
    </p>

    <h4 className="font-bold" style={{ marginTop: "36px" }}>
      Shandong, Liaoning, Fujian (big-base logic).
    </h4>
    <ul>
      <li>
        <b>Shandong</b> has launched deep-sea bases and is moving projects
        through approvals and board decisions; analysts show a rising
        installation path to 2026 under the provincial action plan.
      </li>
      <li>
        <b>Liaoning</b>'s deep-sea ambitions are codified in planning (~13.1 GW
        provincial total); national policy encourages HVDC clusters to haul
        power from the BoHai/Yellow Sea rim.
      </li>
      <li>
        <b>Fujian</b> designated 4.8 GW of deep-sea demonstration within "14FYP"
        and is pushing Changle offshore (multiple blocks) plus Pingtan pilots;
        the province also ran a world-first direct seawater electrolysis
        hydrogen sea-trial—useful for future "energy-island" hybrids.
      </li>
    </ul>

    <h4 className="font-bold" style={{ marginTop: "36px" }}>
      Guangxi & Hainan (demonstration windows).
    </h4>
    <ul>
      <li>
        <b>Guangxi</b> packaged 13.4 GW of deep-sea plots (L/M/N/P blocks) and
        tendered pre-FEED works in 2023, with multiple N-block floating concepts
        listed by third-party trackers.
      </li>
      <li>
        <b>Hainan</b>'s planning has long earmarked 3 GW of demonstration sites
        with ~1.2 GW by 2025; the island is also re-mapping its site plan to
        enable larger deep-sea rounds in "15FYP".
      </li>
    </ul>

    <h3>Technology to watch (and why it matters)</h3>
    <ul>
      <li>
        <b>Flexible HVDC (±500 kV) export</b> is now showing up in real EPCs
        (e.g., Qingzhou-5/7; Sanzhandao). Expect rapid learning curves on
        installation and protection systems as arrays head 70-120 km offshore.
      </li>
      <li>
        <b>Floating</b> is shifting from prototypes to early commercial pilots
        sized for typhoon seas (e.g., 16-17 MW class). This unlocks sites beyond
        the 50 m isobath and is essential to meet long-term GW targets along the
        South China Sea rim.
      </li>
      <li>
        <b>Hybrid platforms (power-to-X)</b>—Fujian's offshore direct seawater
        electrolysis test hints at future "energy-island" hubs combining wind,
        storage and hydrogen to smooth export constraints.
      </li>
    </ul>

    <h3>Comments</h3>
    <ul>
      <li>
        <b>2025 is a construction pivot, not the finish line.</b> The pipeline
        is real—but execution will hinge on cable and converter supply, weather
        windows and heavy-lift availability. Cable houses note a step-up to 500
        kV AC/DC deliveries from 2024-2026, which will be the pacing item for
        many deep-sea clusters.
      </li>
      <li>
        <b>Typhoon engineering is now central to LCOE.</b> Designs are moving to
        Class-S/typhoon-grade loads and revised shutdown/restart envelopes; this
        is where China's OEMs can differentiate in the Western Pacific basin.
      </li>
      <li>
        <b>Price signals differ from Europe.</b> Guangdong's 2023 scheme
        explicitly did not pit bids on tariff; instead, projects reference the
        coal benchmark price, shifting competition to deliverability,
        localisation and grid solutions. That's sensible while HVDC and floating
        are scaling.
      </li>
      <li>
        <b>2030 outlook:</b> Industry and academic forecasts for China's overall
        offshore wind suggest &gt; 65-140 GW cumulative by 2030 depending on
        scenario. A deep-sea (50 m+) slice exceeding ~60 GW by 2030 is plausible
        if HVDC clusters and floating pilots scale on schedule—but it is not a
        formal national target. Treat as an upside case tied to supply-chain
        throughput.
      </li>
    </ul>

    <p className="mt-10">
      On today's trajectory, 2025-2027 should see the first HVDC + deep-sea
      clusters land, while floating moves from prototypes to first arrays. If
      cable/converter capacity keeps pace, a deep-sea installed base on the
      order of ~60 GW by 2030 is within reach—not guaranteed, but supported by
      policy direction, provincial pipelines and visible orders.
    </p>

    <br />
    <br />
    <br />
    <p>
      As the global offshore wind sector continues to advance, innovation,
      investments, and supportive policies are driving transformative growth. At{" "}
      <i>
        <b>WeBridge</b>
      </i>
      , we provide the expertise and insights our partners need to confidently
      navigate this evolving landscape.
    </p>
    <h3 style={{ marginTop: "200px" }}>Subscribe</h3>
    <ContactSubscribe />
  </div>,
);
